import { useEffect, useRef, useState } from "react";
import {
  AccessibilityLegalContent,
  CompanyLegalContent,
  OpenSourceLegalContent,
  PrivacyLegalContent,
} from "./legalContent";

export type LegalPageId = "privacy" | "accessibility" | "open-source" | "company";

export interface LegalPagesViewerProps {
  /** Pagina da mostrare; null = chiuso. */
  page: LegalPageId | null;
  /** Chiude il viewer. */
  onClose: () => void;
}

export const LEGAL_PAGE_META: Record<LegalPageId, { title: string; icon: string }> = {
  privacy: { title: "Privacy & Cookie Policy", icon: "🔒" },
  accessibility: { title: "Dichiarazione di Accessibilità", icon: "♿" },
  "open-source": { title: "Open Source e Trasparenza", icon: "🛠️" },
  company: { title: "La Società", icon: "🏛️" },
};

const PAGE_ORDER: LegalPageId[] = ["privacy", "accessibility", "open-source", "company"];

function PageContent({ page }: { page: LegalPageId }) {
  switch (page) {
    case "privacy":
      return <PrivacyLegalContent />;
    case "accessibility":
      return <AccessibilityLegalContent />;
    case "open-source":
      return <OpenSourceLegalContent />;
    case "company":
      return <CompanyLegalContent />;
  }
}

/**
 * Viewer a schermo intero per le pagine legali/informative (Privacy,
 * Accessibilità, Open Source, La Società). Usato dal footer standard
 * (`TL4KAppFooter`) di tutte le app standalone che non hanno rotte dedicate.
 *
 * Accessibilità: dialog ARIA, chiusura con ESC, focus visibile, blocco dello
 * scroll di sfondo, navigazione tra le pagine nel footer del viewer.
 */
export function LegalPagesViewer({ page, onClose }: LegalPagesViewerProps) {
  const [activePage, setActivePage] = useState<LegalPageId | null>(page);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const closeButtonRef = useRef<HTMLButtonElement | null>(null);

  // Sincronizza lo stato interno quando il genitore cambia pagina/chiude.
  useEffect(() => {
    setActivePage(page);
  }, [page]);

  const meta = activePage ? LEGAL_PAGE_META[activePage] : null;

  // Focus management (WCAG 2.4.3): sposta il focus sul pulsante di chiusura
  // all'apertura e lo ripristina sull'elemento che ha aperto il viewer alla
  // chiusura. Blocca lo scroll di sfondo e chiude con ESC.
  useEffect(() => {
    if (!activePage) return;

    const previousActiveElement = document.activeElement as HTMLElement | null;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    closeButtonRef.current?.focus();

    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === "Escape") onClose();
    };

    window.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", onKeyDown);
      previousActiveElement?.focus?.();
    };
  }, [activePage, onClose]);

  if (!activePage || !meta) return null;

  const year = new Date().getFullYear();

  const openPage = (next: LegalPageId): void => {
    setActivePage(next);
    scrollRef.current?.scrollTo({ top: 0 });
  };

  return (
    <div
      className="tl4k-legal-viewer"
      role="dialog"
      aria-modal="true"
      aria-label={meta.title}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 10000,
        display: "flex",
        flexDirection: "column",
        backgroundColor: "var(--tl4k-surface-warm, #f9f6f5)",
        color: "var(--tl4k-neutral-700, #4a4949)",
        fontFamily: "var(--tl4k-font-family-body, sans-serif)",
        overflow: "hidden",
      }}
    >
      {/* Header */}
      <header
        style={{
          flexShrink: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "var(--tl4k-spacing-md, 12px)",
          padding: "var(--tl4k-spacing-md, 12px) var(--tl4k-spacing-xl, 24px)",
          backgroundColor: "var(--tl4k-surface-default, #ffffff)",
          borderBottom: "1px solid var(--tl4k-neutral-200, #eeeeee)",
          boxShadow: "var(--tl4k-shadow-sm, 0 1px 3px rgba(0,0,0,0.06))",
        }}
      >
        <h1
          style={{
            margin: 0,
            fontSize: "var(--tl4k-font-size-subheading, 22px)",
            fontFamily: "var(--tl4k-font-family-headline, inherit)",
            color: "var(--tl4k-neutral-800, #3a3939)",
          }}
        >
          <span aria-hidden="true">{meta.icon} </span>
          {meta.title}
        </h1>
        <button
          ref={closeButtonRef}
          type="button"
          onClick={onClose}
          aria-label="Chiudi"
          style={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            minWidth: "44px",
            minHeight: "44px",
            padding: "var(--tl4k-spacing-sm, 8px)",
            border: "1px solid var(--tl4k-neutral-200, #eeeeee)",
            borderRadius: "var(--tl4k-border-radius-md, 10px)",
            backgroundColor: "var(--tl4k-surface-default, #ffffff)",
            color: "var(--tl4k-primary-700, #005a80)",
            fontSize: "22px",
            lineHeight: 1,
            cursor: "pointer",
          }}
        >
          ×
        </button>
      </header>

      {/* Contenuto */}
      <main
        ref={scrollRef}
        style={{
          flex: 1,
          overflowY: "auto",
          WebkitOverflowScrolling: "touch",
        }}
      >
        <div
          className="tl4k-legal-content"
          style={{
            maxWidth: "880px",
            margin: "0 auto",
            padding:
              "var(--tl4k-spacing-xl, 24px) var(--tl4k-spacing-xl, 24px) var(--tl4k-spacing-3xl, 40px)",
            display: "flex",
            flexDirection: "column",
            gap: "var(--tl4k-spacing-lg, 16px)",
            fontSize: "var(--tl4k-font-size-body, 20px)",
            lineHeight: 1.6,
          }}
        >
          <PageContent page={activePage} />
        </div>
      </main>

      {/* Footer con navigazione tra le pagine */}
      <footer
        role="contentinfo"
        aria-label="Informazioni legali"
        style={{
          flexShrink: 0,
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "var(--tl4k-spacing-sm, 8px)",
          padding: "var(--tl4k-spacing-sm, 8px) var(--tl4k-spacing-xl, 24px)",
          backgroundColor: "var(--tl4k-neutral-100, #f5f5f5)",
          borderTop: "1px solid var(--tl4k-neutral-200, #eeeeee)",
          fontSize: "var(--tl4k-font-size-small, 18px)",
          color: "var(--tl4k-neutral-600, #6b6a6a)",
        }}
      >
        <span>© {year} TechLAB4Kids S.r.l. Impresa Sociale</span>
        <nav
          aria-label="Altre pagine legali"
          style={{ display: "flex", flexWrap: "wrap", gap: "var(--tl4k-spacing-lg, 16px)" }}
        >
          {PAGE_ORDER.filter((id) => id !== activePage).map((id) => (
            <button
              key={id}
              type="button"
              onClick={() => openPage(id)}
              style={{
                background: "none",
                border: "none",
                padding: 0,
                cursor: "pointer",
                fontFamily: "inherit",
                fontSize: "inherit",
                color: "var(--tl4k-primary-700, #005a80)",
                textDecoration: "underline",
              }}
            >
              {LEGAL_PAGE_META[id].title}
            </button>
          ))}
        </nav>
      </footer>

      <style>{`
        .tl4k-legal-viewer a:focus-visible,
        .tl4k-legal-viewer button:focus-visible {
          outline: 3px solid var(--tl4k-secondary-500, #f39200);
          outline-offset: 2px;
        }
      `}</style>
    </div>
  );
}
